import { useState } from "react";
import { ShieldCheck, Pencil, Lock, Package, ArrowRight, AlertTriangle } from "lucide-react";
import styles from "./PackageServices.module.css";
import EditVatSettingModal from "../../../../components/Finance/Payments/EditVatSettingModal";

const SCENARIO_ROWS = [
  {
    id: 1,
    scenario: "Intermediary + BE Agency",
    role: "Intermediary",
    agency: "Belgian travel agency",
    destination: "Any",
    vat: "0",
    editable: true,
    regime: "NORMAL_VAT_BE",
    remarks: "Commission-based, no TOMS. VAT base = commission only.",
  },
  {
    id: 2,
    scenario: "Intermediary + EU Agency",
    role: "Intermediary",
    agency: "EU travel agency",
    destination: "Any",
    vat: "21",
    editable: true,
    regime: "NORMAL_VAT_BE",
    remarks: "Belgian VAT on intermediary commission.",
  },
  {
    id: 3,
    scenario: "Tour Operator — EU Destination",
    role: "Tour Operator",
    agency: "—",
    destination: "EU",
    vat: "21",
    editable: true,
    regime: "TOMS",
    remarks: "TOMS margin scheme. VAT on total profit margin.",
  },
  {
    id: 4,
    scenario: "Tour Operator — Non-EU Destination",
    role: "Tour Operator",
    agency: "—",
    destination: "Non-EU",
    vat: "0",
    editable: true,
    regime: "TOMS",
    remarks: "VAT exempt under TOMS for non-EU destinations.",
  },
];

const MIXED_EXAMPLE = {
  total: "€ 2.000",
  euPortion: "60%",
  nonEuPortion: "40%",
  euMargin: "€ 720",
  nonEuMargin: "€ 480",
  euVat: "€ 151,20",
  nonEuVat: "€ 0,00",
  totalVat: "€ 151,20",
};

const formatValue = (val) => {
  if (!val && val !== 0 && val !== "0") return "—";
  return `${val}%`;
};

const PackageServices = () => {
  const [showEditModal, setShowEditModal] = useState(false);
  const [editSetting, setEditSetting] = useState(null);

  const handleEdit = (row) => {
    setEditSetting({
      ...row,
      field: "VAT",
      product: row.scenario,
    });
    setShowEditModal(true);
  };

  const handleSave = (payload) => {
    console.log("Save Package Service VAT:", editSetting, payload);
    setShowEditModal(false);
  };

  return (
    <div className={styles.container}>
      <div className={styles.toolbar}>
        <div className={styles.titleSection}>
          <div className={styles.title}>Package Services</div>
          <span className={styles.subtitle}>VAT on Total Profit Margin — 7.4.3</span>
        </div>
      </div>

      <div className={styles.infoBar}>
        <span className={styles.infoIcon} aria-hidden>
          <ShieldCheck size={18} />
        </span>
        <div className={styles.infoContent}>
          <span className={styles.infoTitle}>System Enforcement — Binding</span>
          <span className={styles.infoText}>
            For package services, VAT applies only on the total profit margin — never per product.
            The VAT regime is determined by the VAT decision tree. Parameters here affect calculation,
            not the legal regime assignment.
          </span>
        </div>
      </div>

      <div className={styles.principleCards}>
        <div className={styles.principleCard}>
          <Package size={18} />
          <div>
            <span className={styles.principleTitle}>Booking-Level Margin</span>
            <span className={styles.principleText}>VAT is calculated on the total margin of the entire package booking, not on individual products.</span>
          </div>
        </div>
        <div className={styles.principleCard}>
          <Lock size={18} />
          <div>
            <span className={styles.principleTitle}>System-Derived Regime</span>
            <span className={styles.principleText}>TOMS, NORMAL_VAT_BE, or VAT_EXEMPT is assigned automatically by the decision tree.</span>
          </div>
        </div>
      </div>

      <div className={styles.sectionHeader}>
        <span className={styles.sectionTitle}>VAT Scenario Definitions</span>
      </div>

      <div className={styles.tableSection}>
        <div className={styles.tableCard}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>SCENARIO</th>
                <th>ROLE</th>
                <th>AGENCY TYPE</th>
                <th>DESTINATION</th>
                <th>VAT %</th>
                <th>VAT REGIME</th>
                <th>EDITABLE</th>
                <th>REMARKS</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {SCENARIO_ROWS.map((row) => (
                <tr key={row.id}>
                  <td className={styles.scenarioName}>{row.scenario}</td>
                  <td>
                    <span className={`${styles.roleBadge} ${styles[row.role === "Intermediary" ? "intermediary" : "tourOp"]}`}>
                      {row.role}
                    </span>
                  </td>
                  <td className={styles.agencyCell}>{row.agency}</td>
                  <td>
                    <span className={`${styles.destBadge} ${styles[row.destination === "EU" ? "destEu" : row.destination === "Non-EU" ? "destNonEu" : "destAny"]}`}>
                      {row.destination}
                    </span>
                  </td>
                  <td className={styles.vatValue}>{formatValue(row.vat)}</td>
                  <td>
                    <span className={styles.regimeBadge}>{row.regime}</span>
                  </td>
                  <td>
                    {row.editable ? (
                      <span className={styles.editableYes}>Yes</span>
                    ) : (
                      <span className={styles.editableNo}><Lock size={11} /> No</span>
                    )}
                  </td>
                  <td className={styles.remarks}>{row.remarks}</td>
                  <td className={styles.rowActions}>
                    {row.editable && (
                      <button className={styles.editBtn} onClick={() => handleEdit(row)}>
                        <Pencil size={12} /> Edit
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className={styles.sectionHeader}>
        <span className={styles.sectionTitle}>MIXED Destination — Pro Rata Rule</span>
        <span className={styles.sectionBadge}>7.4.X</span>
      </div>

      <div className={styles.mixedCard}>
        <div className={styles.mixedHeader}>
          <AlertTriangle size={16} />
          <span>When a package includes both EU and Non-EU destinations, VAT is calculated using a pro rata allocation based on the portion of services in each zone.</span>
        </div>

        <div className={styles.mixedExample}>
          <div className={styles.mixedTitle}>Example: Pro Rata Calculation</div>
          <div className={styles.mixedFlow}>
            <div className={styles.mixedStep}>
              <span className={styles.mixedStepLabel}>Total Margin</span>
              <span className={styles.mixedStepValue}>{MIXED_EXAMPLE.total}</span>
            </div>
            <ArrowRight size={16} className={styles.mixedArrow} />
            <div className={styles.mixedSplit}>
              <div className={styles.mixedSplitItem}>
                <span className={styles.splitLabel}>EU Portion ({MIXED_EXAMPLE.euPortion})</span>
                <span className={styles.splitMargin}>Margin: {MIXED_EXAMPLE.euMargin}</span>
                <span className={styles.splitVat}>VAT 21%: {MIXED_EXAMPLE.euVat}</span>
              </div>
              <div className={styles.mixedSplitItem}>
                <span className={styles.splitLabel}>Non-EU Portion ({MIXED_EXAMPLE.nonEuPortion})</span>
                <span className={styles.splitMargin}>Margin: {MIXED_EXAMPLE.nonEuMargin}</span>
                <span className={styles.splitVatExempt}>VAT 0%: {MIXED_EXAMPLE.nonEuVat}</span>
              </div>
            </div>
            <ArrowRight size={16} className={styles.mixedArrow} />
            <div className={styles.mixedStep}>
              <span className={styles.mixedStepLabel}>Total VAT Due</span>
              <span className={styles.mixedStepValueFinal}>{MIXED_EXAMPLE.totalVat}</span>
            </div>
          </div>
        </div>
      </div>

      <div className={styles.ruleCards}>
        <div className={styles.ruleCard}>
          <span className={styles.ruleIcon}>📦</span>
          <div>
            <span className={styles.ruleTitle}>Package VAT — Binding Rule</span>
            <span className={styles.ruleText}>
              VAT never applies per product within a package. It is always calculated at the booking-level margin.
              The VAT decision tree determines the regime. Changing parameters here only affects future calculations.
            </span>
          </div>
        </div>
        <div className={styles.ruleCard}>
          <span className={styles.ruleIcon}>⚖️</span>
          <div>
            <span className={styles.ruleTitle}>Terminology — Binding Rule</span>
            <span className={styles.ruleText}>
              The term "Margin" must always be labeled as "TOMS Margin (VAT basis)" in this context.
              Never use "Margin" generically. Commission applies only to intermediary (agent) scenarios.
            </span>
          </div>
        </div>
      </div>

      <EditVatSettingModal
        open={showEditModal}
        onClose={() => setShowEditModal(false)}
        setting={editSetting}
        onSave={handleSave}
        saveLoading={false}
      />
    </div>
  );
};

export default PackageServices;